import { useMemo, useState, type ReactNode } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import './FinancialDataTable.css';

type SortKind = 'text' | 'number' | 'decimal' | 'date';
type SortValue = string | number | null | undefined;

export interface FinancialColumn<Row> {
  id: string;
  label: ReactNode;
  menuLabel: string;
  help?: ReactNode;
  defaultVisible?: boolean;
  cell: (row: Row) => ReactNode;
  sort?: { kind: SortKind; value: (row: Row) => SortValue };
}

interface FinancialDataTableProps<Row> {
  id: string;
  rows: Row[];
  columns: FinancialColumn<Row>[];
  rowKey: (row: Row) => string;
  className?: string;
  ariaLabel: string;
  paginated?: boolean;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function decimalParts(value: string): { negative: boolean; digits: string; scale: number } | null {
  const match = /^(-?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(value);
  if (!match) return null;
  const exponent = Number(match[4] || 0);
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 1000) return null;
  const digits = `${match[2]}${match[3] || ''}`.replace(/^0+(?=\d)/, '');
  return { negative: match[1] === '-' && /[1-9]/.test(digits), digits, scale: (match[3] || '').length - exponent };
}

function compareDecimals(left: string, right: string): number {
  const a = decimalParts(left);
  const b = decimalParts(right);
  if (!a || !b) return collator.compare(left, right);
  if (a.negative !== b.negative) return a.negative ? -1 : 1;
  const scale = Math.max(a.scale, b.scale, 0);
  const aDigits = (a.digits + '0'.repeat(scale - a.scale)).replace(/^0+(?=\d)/, '');
  const bDigits = (b.digits + '0'.repeat(scale - b.scale)).replace(/^0+(?=\d)/, '');
  const result = aDigits.length - bDigits.length || (aDigits < bDigits ? -1 : aDigits > bDigits ? 1 : 0);
  return a.negative ? -result : result;
}

function compareValues(left: SortValue, right: SortValue, kind: SortKind, direction: 'ascending' | 'descending'): number {
  // Missing evidence stays at the end in either direction.
  if (left === null || left === undefined || left === '') return right === null || right === undefined || right === '' ? 0 : 1;
  if (right === null || right === undefined || right === '') return -1;
  if (kind === 'date') {
    const leftTime = Date.parse(String(left));
    const rightTime = Date.parse(String(right));
    if (!Number.isFinite(leftTime)) return Number.isFinite(rightTime) ? 1 : 0;
    if (!Number.isFinite(rightTime)) return -1;
    return (leftTime - rightTime) * (direction === 'ascending' ? 1 : -1);
  }
  const result = kind === 'decimal' ? compareDecimals(String(left), String(right))
    : kind === 'number' ? Number(left) - Number(right)
    : collator.compare(String(left), String(right));
  return (Number.isNaN(result) ? 0 : result) * (direction === 'ascending' ? 1 : -1);
}

function readVisibleColumns<Row>(id: string, columns: FinancialColumn<Row>[]): string[] {
  const columnIds = columns.map((column) => column.id);
  try {
    const stored = JSON.parse(window.localStorage.getItem(`financial-table-columns:${id}`) || 'null');
    if (Array.isArray(stored)) {
      const visible = columnIds.filter((column) => stored.includes(column));
      if (visible.length) return visible;
    }
  } catch { /* Storage may be disabled. */ }
  const defaults = columns.filter((column) => column.defaultVisible !== false).map((column) => column.id);
  return defaults.length ? defaults : columnIds;
}

export default function FinancialDataTable<Row>({ id, rows, columns, rowKey, className = '', ariaLabel, paginated = false }: FinancialDataTableProps<Row>) {
  const [visibleIds, setVisibleIds] = useState(() => readVisibleColumns(id, columns));
  const [sort, setSort] = useState<{ id: string; direction: 'ascending' | 'descending' } | null>(() => {
    const dateColumn = columns.find(column => column.sort?.kind === 'date');
    return dateColumn ? { id: dateColumn.id, direction: 'descending' } : null;
  });
  const visibleColumns = columns.filter((column) => visibleIds.includes(column.id));
  const selectedColumn = columns.find((column) => column.id === sort?.id && column.sort);
  const displayedRows = useMemo(() => {
    if (!selectedColumn?.sort || !sort) return rows;
    return rows.map((row, index) => ({ row, index })).sort((a, b) =>
      compareValues(selectedColumn.sort!.value(a.row), selectedColumn.sort!.value(b.row), selectedColumn.sort!.kind, sort.direction) || a.index - b.index,
    ).map(({ row }) => row);
  }, [rows, selectedColumn, sort]);

  const toggleColumn = (idToToggle: string) => {
    const next = visibleIds.includes(idToToggle) ? visibleIds.filter((value) => value !== idToToggle) : [...visibleIds, idToToggle];
    if (!next.length) return;
    setVisibleIds(next);
    try { window.localStorage.setItem(`financial-table-columns:${id}`, JSON.stringify(next)); } catch { /* Keep this session's choice. */ }
  };

  const sortDescription = selectedColumn && sort
    ? `${selectedColumn.menuLabel}: ${selectedColumn.sort?.kind === 'date' ? (sort.direction === 'descending' ? 'newest first' : 'oldest first') : sort.direction}${visibleIds.includes(selectedColumn.id) ? '' : ' (hidden column)'}. `
    : '';

  return <>
    <div className="financial-table-controls">
      <span role="status">{sortDescription}{paginated ? 'Sorting applies to this page only' : 'Click a heading to sort'}</span>
      <details className="financial-column-menu">
        <summary>Columns</summary>
        <div role="group" aria-label={`${ariaLabel} columns`}>
          {columns.map((column) => <label key={column.id}><input type="checkbox" checked={visibleIds.includes(column.id)} disabled={visibleIds.length === 1 && visibleIds.includes(column.id)} onChange={() => toggleColumn(column.id)} />{column.menuLabel}</label>)}
        </div>
      </details>
    </div>
    <div className="financial-table-wrap" tabIndex={0} role="region" aria-label={`${ariaLabel}, scroll horizontally for all columns`}>
      <table className={`financial-table ${className}`} style={visibleColumns.length < columns.length ? { minWidth: `${Math.max(280, visibleColumns.length * 170)}px` } : undefined}>
        <thead><tr>{visibleColumns.map((column) => <th key={column.id} className={column.sort?.kind === 'number' || column.sort?.kind === 'decimal' ? 'financial-number' : undefined} scope="col" aria-sort={sort?.id === column.id ? sort.direction : undefined}>
          {column.sort ? <span className="financial-sort-heading"><button type="button" className="financial-sort-button" onClick={() => setSort((current) => ({ id: column.id, direction: current?.id === column.id ? (current.direction === 'ascending' ? 'descending' : 'ascending') : (column.sort?.kind === 'date' ? 'descending' : 'ascending') }))} aria-label={`Sort by ${column.menuLabel}${sort?.id === column.id ? `, currently ${sort.direction}` : ''}`}>
            {column.label}<span className="financial-sort-icon" aria-hidden="true">{sort?.id === column.id && sort.direction === 'ascending' ? <FiChevronUp /> : <FiChevronDown />}</span>
          </button>{column.help}</span> : <span className="financial-sort-heading">{column.label}{column.help}</span>}
        </th>)}</tr></thead>
        <tbody>{displayedRows.map((row) => <tr key={rowKey(row)}>{visibleColumns.map((column) => <td key={column.id} className={column.sort?.kind === 'number' || column.sort?.kind === 'decimal' ? 'financial-number' : undefined}>{column.cell(row)}</td>)}</tr>)}</tbody>
      </table>
    </div>
  </>;
}
